import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const stats = [
  { value: '+500', label: 'Eventos Realizados' },
  { value: '150k', label: 'Participantes Atendidos' },
  { value: '98%', label: 'Satisfação dos Clientes' },
  { value: '+50', label: 'Cidades no Brasil' },
];

const steps = [
  {
    number: '01',
    icon: 'ri-broadcast-line',
    title: 'Palestrante Fala Normalmente',
    description: 'O palestrante usa um microfone sem fio comum. Sem cabines, sem equipamentos extras.',
  },
  {
    number: '02',
    icon: 'ri-wifi-line',
    title: 'Sinal Transmitido em RF',
    description: 'O transmissor envia o áudio em radiofrequência para todos os fones simultaneamente com latência &lt; 5ms.',
  },
  {
    number: '03',
    icon: 'ri-headphone-line',
    title: 'Participante Escolhe o Canal',
    description: 'Cada pessoa seleciona o canal desejado no fone — palestrante, idioma ou DJ — e ajusta o próprio volume.',
  },
  {
    number: '04',
    icon: 'ri-star-smile-line',
    title: 'Experiência Imersiva',
    description: 'Áudio de alta fidelidade direto ao ouvido. Sem reverberação, sem barulho ambiente, sem distrações.',
  },
];

export default function WhatIsSection() {
  const { ref: introRef, isVisible: introVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: stepsRef, isVisible: stepsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <>
      {/* Stats strip */}
      <section ref={statsRef} className="py-14 bg-secondary">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div
                key={i}
                className={`text-center transition-all duration-500 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="font-heading font-extrabold text-4xl md:text-5xl text-primary mb-2">{stat.value}</div>
                <div className="font-body text-sm text-white/70 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is section */}
      <section ref={introRef} className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Text */}
            <div className={`transition-all duration-700 ${introVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
                O que é
              </span>
              <h2 className="font-heading font-bold text-5xl text-secondary mb-6 leading-tight">
                palestra silenciosa<br />em eventos
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-6">
                <strong className="text-secondary">Palestra silenciosa</strong> é a tecnologia em que o áudio do palestrante ou DJ chega diretamente ao ouvido de cada participante por fones sem fio — em vez de caixas de som convencionais.
              </p>
              <p className="font-body text-base text-gray-600 leading-relaxed mb-6">
                O resultado: zero ruído ambiente, até <strong>até 10 sessões simultâneas</strong> no mesmo espaço, suporte multilíngue sem cabines e controle individual de volume para cada pessoa.
              </p>
              <p className="font-body text-base text-gray-600 leading-relaxed">
                Ideal para <strong>eventos corporativos</strong>, conferências internacionais, treinamentos de RH, casamentos, festivais e qualquer situação em que o áudio de qualidade é determinante para o sucesso do evento.
              </p>
            </div>

            {/* Image */}
            <div className={`relative transition-all duration-700 delay-200 ${introVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="w-full h-96 rounded-3xl overflow-hidden">
                <img
                  src="/img/stib/fone-participante-close.webp"
                  alt="Participante usando fone sem fio em palestra silenciosa"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10">
                    <i className="ri-signal-wifi-line text-primary text-2xl" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-secondary text-sm">Latência &lt; 5ms</div>
                    <div className="font-body text-xs text-gray-500">Áudio em tempo real</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section ref={stepsRef} className="py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
              Processo
            </span>
            <h2 className={`font-heading font-bold text-5xl text-secondary transition-all duration-700 ${stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              como funciona
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`relative bg-white rounded-3xl p-8 border border-gray-100 transition-all duration-700 ${stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {/* Arrow connector */}
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 -right-4 z-10">
                    <i className="ri-arrow-right-line text-primary text-2xl" />
                  </div>
                )}
                <div className="font-heading font-extrabold text-5xl text-primary/15 mb-4 leading-none">{step.number}</div>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary/10 mb-4">
                  <i className={`${step.icon} text-primary text-2xl`} />
                </div>
                <h3 className="font-heading font-bold text-lg text-secondary mb-3 leading-snug">{step.title}</h3>
                <p
                  className="font-body text-sm text-gray-600 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: step.description }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
